import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Subject, merge } from 'rxjs';
import { auditTime, map, startWith, switchMap, tap } from 'rxjs/operators';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import { OrganizationCreateService } from 'wflow-main-app/src/app/shared/services/organization-create';
import {
  IconsModule,
  SearchInputComponent,
  WuiButtonModule,
  WuiMenuModule,
  WuiTooltipModule,
} from 'wflow-ui';
import {
  CONTEXT_SELECTOR_TOKEN,
  ContextItem,
  OrgGroup,
} from './context-selector.token';

interface DisplayGroup {
  label?: string;
  items: ContextItem[];
}

@Component({
  selector: 'context-selector',
  imports: [
    CommonModule,
    WuiButtonModule,
    IconsModule,
    WuiMenuModule,
    SearchInputComponent,
    TranslateModule,
    RouterModule,
    WuiTooltipModule,
  ],
  templateUrl: './context-selector.component.html',
  styleUrls: ['./context-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'context-selector',
  },
})
export class ContextSelectorComponent {
  readonly contextSvc = inject(CONTEXT_SELECTOR_TOKEN);
  private readonly productTrackingSvc = inject(ProductEventTrackingService);
  private readonly orgCreateSvc = inject(OrganizationCreateService);

  /**
   * Below the threshold the menu drops the search box — over a handful of
   * organizations it is noise that only makes the list harder to scan.
   */
  protected readonly showSearch = computed(() => {
    const svc = this.contextSvc;
    return svc ? svc.total() >= svc.showSearchFromCount : false;
  });

  protected readonly canCreateOrganization = this.orgCreateSvc.canCreate;

  readonly menuPosition: ConnectedPosition = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  };

  readonly onMenuOpen = output<void>();
  readonly onMenuClose = output<void>();

  protected opened$ = new Subject<void>();
  protected search$ = new BehaviorSubject<string>('');
  private readonly current$ = toObservable(this.contextSvc.currentName).pipe(
    tap(() => {
      this.search$.next('');
      this.searchInput?.resetValue();
    }),
  );
  private readonly favorites$ = toObservable(this.contextSvc.favorites);

  protected displayData$ = merge(
    this.search$,
    this.current$,
    this.opened$,
    this.favorites$,
  ).pipe(
    startWith(''),
    auditTime(100),
    switchMap(() => this.contextSvc.getList(this.search$.getValue())),
    map((items) => this.buildGroups(items, !!this.search$.getValue())),
  );

  @ViewChild(SearchInputComponent) searchInput: SearchInputComponent;

  /**
   * A picked organization commits the context change (the option is a routerLink
   * into the org). Re-picking the organization already open is not a switch, so
   * only a different one is counted.
   */
  protected onOrganizationSelect(item: ContextItem) {
    this.search$.next('');

    if (!item.isActive) {
      this.productTrackingSvc.track('nav_org_switched');
    }
  }

  /** The "all organizations" link at the bottom of the selector menu. */
  protected onOrganizationsLinkOpen() {
    this.productTrackingSvc.track('nav_organizations_link_open');
  }

  protected onCreateOrganization() {
    this.orgCreateSvc.startCreate('nav_org_create_open');
  }

  private buildGroups(
    items: ContextItem[],
    isSearching: boolean,
  ): DisplayGroup[] {
    if (isSearching) {
      return items.length ? [{ items }] : [];
    }

    const byGroup = (g: OrgGroup) => items.filter((i) => i.group === g);
    const active = byGroup('active');
    const favorites = byGroup('favorites');
    const others = byGroup('others');

    const groups: DisplayGroup[] = [];
    if (active.length) groups.push({ items: active });
    if (favorites.length)
      groups.push({
        label: 'DASHBOARD.FavoriteOrganizations',
        items: favorites,
      });
    if (others.length) groups.push({ items: others });
    return groups;
  }
}
